// Seitenkopf (Brotkrumen, Fenstertitel) und seitenbezogene Befehle für die Befehlspalette.
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { IconName } from "../components/Icon";
import { T } from "../texte";

export interface Krume {
  label: string;
  to?: string;
}

export interface Befehl {
  id: string;
  label: string;
  icon?: IconName;
  taste?: string;
  gruppe?: string;
  aktion: () => void;
}

interface SeitenWert {
  krumen: Krume[];
  setKrumen: (k: Krume[]) => void;
  befehle: Befehl[];
  setBefehle: (b: Befehl[]) => void;
}

const Kontext = createContext<SeitenWert | null>(null);

export function SeitenProvider({ children }: { children: ReactNode }) {
  const [krumen, setKrumen] = useState<Krume[]>([]);
  const [befehle, setBefehle] = useState<Befehl[]>([]);
  const wert = useMemo(() => ({ krumen, setKrumen, befehle, setBefehle }), [krumen, befehle]);
  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

function useSeitenKontext(): SeitenWert {
  const w = useContext(Kontext);
  if (!w) throw new Error("SeitenProvider fehlt");
  return w;
}

/** Brotkrumen und Fenstertitel der Seite setzen. */
export function useSeitenKopf(krumen: Krume[]): void {
  const { setKrumen } = useSeitenKontext();
  const schluessel = JSON.stringify(krumen);
  useEffect(() => {
    setKrumen(krumen);
    const letzter = krumen[krumen.length - 1]?.label;
    document.title = letzter ? `${letzter} · ${T.app}` : T.app;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel, setKrumen]);
}

export function useKrumen(): Krume[] {
  return useSeitenKontext().krumen;
}

/** Befehle der aktuellen Seite in der Befehlspalette anbieten (z. B. „Notiz schreiben“ auf der Lead-Seite). */
export function useSeitenBefehle(befehle: Befehl[]): void {
  const { setBefehle } = useSeitenKontext();
  const ref = useRef(befehle);
  ref.current = befehle;
  const schluessel = befehle.map((b) => `${b.id}:${b.label}`).join("|");
  useEffect(() => {
    // Aktionen über die Ref aufrufen, damit sie immer den aktuellen Zustand sehen.
    setBefehle(ref.current.map((b) => ({ ...b, aktion: () => ref.current.find((x) => x.id === b.id)?.aktion() })));
    return () => setBefehle([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel, setBefehle]);
}

export function useBefehle(): Befehl[] {
  return useSeitenKontext().befehle;
}
